import { Dashboard, TabGroup, TabsDisplay } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import { v4 as uuidv4 } from "uuid";

interface DashboardTabConfiguratorProps {
  onHide: () => void;
  dashboard: Dashboard;
  setDashboard: (dashboard: Dashboard) => void;
}

const tabLabelHandler = (value: string) => {
  return value === TabsDisplay.ROWS ? T("tabs-display.rows") : T(`tabs-display.dropdown`);
};

const typesOptions = Object.values(TabsDisplay).map(value => ({
  label: tabLabelHandler(value),
  value,
}));

const DashboardConfigurator = ({
  onHide,
  dashboard,
  setDashboard,
}: DashboardTabConfiguratorProps) => {
  const [selectedTabDisplayType, setSelectedTabDisplayType] = useState<TabsDisplay>(
    dashboard.tabsDisplay ?? TabsDisplay.ROWS
  );

  const [currentTabGroups, setCurrentTabGroups] = useState<TabGroup[]>(dashboard.tabGroups ?? []);
  const handleSaveDashboardConfigurator = () => {
    const newDashboard = {
      ...dashboard,
      tabsDisplay: selectedTabDisplayType,
      tabGroups: currentTabGroups,
    };
    setDashboard(newDashboard);
    onHide();
  };

  const onRowEditComplete = (e: any) => {
    const { newData, index } = e;
    setCurrentTabGroups(prevGroups => {
      const updatedGroups = [...prevGroups];
      if (index !== -1) {
        updatedGroups.splice(index, 1, newData);
      }
      return updatedGroups;
    });
  };

  const groupRemoveButtonTemplate = (rowIndex: number) => {
    return (
      <Button
        type="button"
        icon="pi pi-times"
        tooltip={T("action.remove")}
        tooltipOptions={{ position: "bottom" }}
        className="p-button-danger p-button-sm p-button-text"
        onClick={() =>
          confirmDialog({
            message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-group"),
            header: T("confirm-dialog.remove-group"),
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: () => {
              setCurrentTabGroups(prevGroups => prevGroups.toSpliced(rowIndex, 1));
            },
          })
        }
      />
    );
  };

  const dialogFooterTemplate = () => {
    return (
      <div className="flex flex-row gap-2 h-3rem justify-content-end ml-auto">
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={onHide}
          className="p-button-sm p-button-danger"
        />
        <Button
          label={T("action.save")}
          icon="pi pi-check"
          onClick={handleSaveDashboardConfigurator}
          className="p-button-sm p-button-success"
        />
      </div>
    );
  };

  const tabGroupTitleEditorTemplate = (options: any) => {
    return (
      <InputText
        value={options.value}
        type="text"
        className="p-inputtext-sm w-full"
        placeholder={T("action.write-title")}
        name="title"
        onChange={e => {
          options.editorCallback(e.target.value);
        }}
      />
    );
  };

  return (
    <Dialog
      header={T("action.configure-dashboard")}
      visible
      onHide={onHide}
      footer={dialogFooterTemplate}
      className="w-30rem"
      style={{ maxHeight: "80vh" }}
      resizable
      draggable
    >
      <ConfirmDialog />
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-2">
          <label htmlFor="tabsDisplayType">{T("action.change-tabs-display-type")}</label>
          <Dropdown
            className="w-full"
            value={selectedTabDisplayType}
            options={typesOptions}
            placeholder={T("action.change-tabs-display-type")}
            onChange={e => setSelectedTabDisplayType(e.value)}
          />
        </div>
        <div className="flex flex-column gap-2">
          <div className="flex flex-row justify-content-between">
            <label htmlFor="tabGroups">{T("label.tab-groups")}</label>
            <Button
              icon="pi pi-plus"
              className="p-button-sm p-button-success p-button-text"
              tooltip={T("action.add-tab-group")}
              tooltipOptions={{ position: "bottom" }}
              onClick={() =>
                setCurrentTabGroups([...currentTabGroups, { title: "New group", uuid: uuidv4() }])
              }
            />
          </div>
          {currentTabGroups.length === 0 ? (
            <span style={{ textAlign: "center" }}>{T("label.no-tab-groups")}</span>
          ) : (
            <DataTable
              value={currentTabGroups}
              dataKey="uuid"
              className="p-datatable-sm"
              editMode="row"
              onRowEditComplete={onRowEditComplete}
              size="small"
            >
              <Column
                field="title"
                header="Title"
                style={{ width: "80%" }}
                editor={options => tabGroupTitleEditorTemplate(options)}
              />
              <Column
                editor={e => groupRemoveButtonTemplate(e.rowIndex)}
                style={{ width: "10%" }}
              />
              <Column rowEditor style={{ width: "10%" }} />
            </DataTable>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default DashboardConfigurator;
